import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import AddEditClinicModal from "../components/CoCM/AddEditClinicModal";
import DeactivateClinicModal from "../components/CoCM/DeactivateClinicModal";

const clinicStatusColors = {
  Active: { bg: "#D0FAE5", color: "#006145" },
  Inactive: { bg: "#FEE2E2", color: "#991B1B" },
};

const appStatusColors = {
  Linked: { bg: "#D0FAE5", color: "#006145" },
  "Invite Sent": { bg: "#E8F1FF", color: "#2F6FED" },
  "Pending Registration": { bg: "#FFF4E5", color: "#D9822B" },
  "Not Invited": { bg: "#F3F4F6", color: "#6B7280" },
  Revoked: { bg: "#FEE2E2", color: "#991B1B" },
  Expired: { bg: "#FEE2E2", color: "#991B1B" },
};

const StatusPill = ({ status, colorMap = clinicStatusColors }) => {
  const colors = colorMap[status] || { bg: "#F3F4F6", color: "#6B7280" };
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-block",
      }}
    >
      {status}
    </span>
  );
};

const RegistryTabs = ({ activeTab, onTabChange }) => (
  <div className="d-flex gap-2" style={{ padding: "0 0 16px 0" }}>
    {["Active", "Inactive", "Clinics", "Doctors"].map((tab) => (
      <button
        key={tab}
        type="button"
        className={`pill-tab ${activeTab === tab ? "active" : ""}`}
        onClick={() => onTabChange(tab)}
        style={{
          padding: "8px 24px",
          borderRadius: "6px",
          fontWeight: 600,
          fontSize: "13px",
          border: "none",
          backgroundColor: activeTab === tab ? "#27B99A" : "#E9EEF1",
          color: activeTab === tab ? "#FFFFFF" : "#064260",
          cursor: "pointer",
        }}
      >
        {tab}
      </button>
    ))}
  </div>
);

const ClinicDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [rowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchClinic = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-clinic/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setClinic(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching clinic:", error);
      toast.error("Failed to load clinic details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClinic();
  }, [fetchClinic]);

  const appUsersList = clinic?.appUsersList || [];
  const totalPages = Math.ceil(appUsersList.length / rowsPerPage) || 1;
  const paginatedAppUsers = useMemo(
    () =>
      appUsersList.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage,
      ),
    [appUsersList, currentPage, rowsPerPage],
  );

  const handleTabChange = (tab) => {
    if (tab === "Clinics") {
      navigate("/registry-cocm?tab=Clinics");
      return;
    }
    navigate(`/registry-cocm?tab=${tab}`);
  };

  const handleEditClinic = async (name) => {
    if (!clinic) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-clinic/${clinic.id}`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowEditModal(false);
        toast.success("Clinic updated successfully");
        setClinic(response.data.data);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to update clinic. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateClinic = async () => {
    if (!clinic) return;
    setDeactivating(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/deactivate-clinic/${clinic.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowDeactivateModal(false);
        toast.success("Clinic deactivated successfully");
        setClinic(response.data.data);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to deactivate clinic. Please try again.",
      );
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div style={{ padding: "0 0 24px 0" }}>
      <RegistryTabs activeTab="Clinics" onTabChange={handleTabChange} />

      <div
        className="bg-white rounded shadow-sm"
        style={{ padding: "24px 28px 28px" }}
      >
        <button
          type="button"
          onClick={() => navigate("/registry-cocm?tab=Clinics")}
          style={{
            background: "none",
            border: "none",
            color: "#27B99A",
            fontSize: "14px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            marginBottom: "20px",
            padding: 0,
          }}
        >
          <FaArrowLeft size={12} />
          Back
        </button>

        {loading ? (
          <p className="text-muted">Loading clinic details...</p>
        ) : !clinic ? (
          <p className="text-muted">Clinic not found.</p>
        ) : (
          <>
            <div
              className="d-flex justify-content-between align-items-start flex-wrap gap-3"
              style={{ marginBottom: "24px" }}
            >
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h1
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "28px",
                    fontWeight: 700,
                    color: "#111111",
                    margin: 0,
                  }}
                >
                  {clinic.name}
                </h1>
                <StatusPill status={clinic.status} />
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  style={{
                    backgroundColor: "#27B99A",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
                {clinic.status !== "Inactive" && (
                  <button
                    type="button"
                    onClick={() => setShowDeactivateModal(true)}
                    style={{
                      backgroundColor: "#EF4444",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>

            <div
              className="d-flex flex-wrap gap-4"
              style={{ marginBottom: "28px" }}
            >
              <div>
                <div style={{ fontSize: "13px", color: "#8E8E93" }}>
                  Total Patients
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#064260",
                  }}
                >
                  {clinic.totalPatients}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "13px", color: "#8E8E93" }}>
                  Active Patients
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#064260",
                  }}
                >
                  {clinic.activePatients}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "13px", color: "#8E8E93" }}>
                  App Users
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#064260",
                  }}
                >
                  {clinic.appUsers}
                </div>
              </div>
            </div>

            <div
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #EAECEF",
              }}
            >
              <div
                style={{
                  backgroundColor: "#064260",
                  color: "#FFFFFF",
                  padding: "14px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                App Users &amp; Access Codes
              </div>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#064260", color: "#fff" }}>
                      <th
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                        }}
                      >
                        Access Code
                      </th>
                      <th
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                        }}
                      >
                        User Email
                      </th>
                      <th
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                        }}
                      >
                        App Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAppUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            padding: "32px 16px",
                            textAlign: "center",
                            color: "#8E8E93",
                          }}
                        >
                          No app users or access codes found for this clinic.
                        </td>
                      </tr>
                    ) : (
                      paginatedAppUsers.map((user, index) => (
                        <tr
                          key={user.id}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#FFFFFF" : "#F8FAFC",
                          }}
                        >
                          <td
                            style={{
                              padding: "14px 16px",
                              color: "#064260",
                              fontWeight: 600,
                            }}
                          >
                            {user.name}
                          </td>
                          <td
                            style={{
                              padding: "14px 16px",
                              fontFamily: "monospace",
                            }}
                          >
                            {user.accessCode}
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            {user.email}
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            <StatusPill
                              status={user.appStatus}
                              colorMap={appStatusColors}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {appUsersList.length > 0 && (
                <div
                  className="d-flex justify-content-end align-items-center px-3 py-2 gap-2"
                  style={{ fontSize: "13px", color: "#666" }}
                >
                  <span>
                    {(currentPage - 1) * rowsPerPage + 1}–
                    {Math.min(appUsersList.length, currentPage * rowsPerPage)}{" "}
                    of {appUsersList.length}
                  </span>
                  <Button
                    variant="light"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: "6px",
                      width: "32px",
                      height: "32px",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaChevronLeft size={10} />
                  </Button>
                  <Button
                    variant="light"
                    disabled={
                      currentPage === totalPages || appUsersList.length === 0
                    }
                    onClick={() => setCurrentPage((p) => p + 1)}
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: "6px",
                      width: "32px",
                      height: "32px",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaChevronRight size={10} />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showEditModal && clinic && (
        <AddEditClinicModal
          clinic={clinic}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditClinic}
          isSubmitting={saving}
        />
      )}

      {showDeactivateModal && clinic && (
        <DeactivateClinicModal
          clinic={clinic}
          onClose={() => setShowDeactivateModal(false)}
          onConfirm={handleDeactivateClinic}
          isSubmitting={deactivating}
        />
      )}
    </div>
  );
};

export default ClinicDetail;
